import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { ApiError, postAffiliateApply, type AffiliateKind } from '@/lib/kwykedit-api'

export const Route = createFileRoute('/affiliates')({
  component: AffiliatesPage,
})

const KIND_OPTIONS: Array<{ value: AffiliateKind; label: string }> = [
  { value: 'promo-artist', label: 'Promo Artist' },
  { value: 'company', label: 'Company' },
  { value: 'other', label: 'Other' },
]

const fieldClassName =
  'w-full rounded-md border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-white placeholder:text-neutral-500 focus:border-neutral-400 focus:outline-none'

function AffiliatesPage() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [kind, setKind] = useState<AffiliateKind>('promo-artist')
  const [about, setAbout] = useState('')
  const [heardFrom, setHeardFrom] = useState('')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (pending) return
    setPending(true)
    setError(null)
    void postAffiliateApply({ name: name.trim(), email: email.trim(), kind, about: about.trim(), heard_from: heardFrom.trim() })
      .then(() => setSent(true))
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not send your application.')
      })
      .finally(() => setPending(false))
  }

  return (
    <div className="min-h-screen bg-black text-neutral-100 select-text">
      <header className="border-b border-neutral-800">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-3" aria-label="KWYKEDIT home">
            <img src="/brand/logo.png" alt="KWYKEDIT" className="h-8 w-auto" />
          </a>
          <nav className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm" className="text-neutral-300 hover:text-white">
              <a href="/login">Log in</a>
            </Button>
            <Button asChild size="sm" className="bg-white text-black hover:bg-neutral-200">
              <a href="/signup">Sign up</a>
            </Button>
          </nav>
        </div>
      </header>

      <div className="mx-auto max-w-xl px-6 py-12">
        <h1 className="mb-2 text-3xl font-bold tracking-tight text-white">Affiliate Program</h1>
        <p className="mb-8 text-neutral-400">
          Promote KWYKEDIT to your audience and earn from every signup. Tell us who you
          are and we will be in touch.
        </p>

        {sent ? (
          <div className="rounded-xl border border-neutral-700 bg-neutral-950 p-8 text-center">
            <p className="mb-2 text-lg font-semibold text-white">Application received</p>
            <p className="text-sm text-neutral-400">
              Thanks — we review every application personally and will reply by email.
            </p>
            <p className="mt-6">
              <Link to="/" className="text-sm text-neutral-300 underline-offset-4 hover:text-white hover:underline">
                ← Back to home
              </Link>
            </p>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="space-y-4 rounded-xl border border-neutral-800 bg-neutral-950 p-6"
          >
            <label className="block text-sm font-medium text-neutral-200">
              Name
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                autoComplete="name"
                className={`mt-1 ${fieldClassName}`}
              />
            </label>
            <label className="block text-sm font-medium text-neutral-200">
              Email address
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className={`mt-1 ${fieldClassName}`}
              />
            </label>
            <label className="block text-sm font-medium text-neutral-200">
              I am applying as a
              <select value={kind} onChange={(e) => setKind(e.target.value as AffiliateKind)} className={`mt-1 ${fieldClassName}`}>
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-medium text-neutral-200">
              About you
              <span className="mt-0.5 block text-xs font-normal text-neutral-500">
                A few lines about you as a promo artist, company, or anything else.
              </span>
              <textarea
                value={about}
                onChange={(e) => setAbout(e.target.value)}
                placeholder="Audience size, where you post, why KWYKEDIT fits…"
                rows={5}
                className={`mt-1 ${fieldClassName}`}
              />
            </label>
            <label className="block text-sm font-medium text-neutral-200">
              How did you hear about us?
              <input
                value={heardFrom}
                onChange={(e) => setHeardFrom(e.target.value)}
                placeholder="e.g. YouTube, a friend, search…"
                className={`mt-1 ${fieldClassName}`}
              />
            </label>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <Button type="submit" disabled={pending} className="w-full bg-white text-black hover:bg-neutral-200">
              {pending ? 'Sending…' : 'Apply'}
            </Button>
          </form>
        )}
      </div>
    </div>
  )
}
